/**
 * Super-Capacitor Calculations Panel Component
 * Based on IECON2023 Paper: Design Criteria for Super-Capacitor Modules
 * University of Innsbruck (i-PEL), Hanschek et al.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
  useTheme,
  Skeleton,
  Grid,
  Divider,
  Chip,
  Tooltip
} from '@mui/material';
import {
  ElectricBolt,
  Battery80,
  Engineering,
  BarChart,
  Info
} from '@mui/icons-material';
import apiClient from '../services/api';
import PowerProfileChart from './PowerProfileChart';
import SupercapPowerChart from './SupercapPowerChart';
import BoxPlotChart from './BoxPlotChart';
import PowerHistogram from './PowerHistogram';
import BreakingEnergyChart from './BreakingEnergyChart';
import EnergyRatioChart from './EnergyRatioChart';
import VoltageLifetimeChart from './VoltageLifetimeChart';
import ZoomableCanvas from './ZoomableCanvas';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`supercap-tabpanel-${index}`}
      aria-labelledby={`supercap-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `supercap-tab-${index}`,
    'aria-controls': `supercap-tabpanel-${index}`,
  };
}

interface SupercapCalculationsPanelProps {
  uploadId: string;
}

const SupercapCalculationsPanel: React.FC<SupercapCalculationsPanelProps> = ({ uploadId }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [calculationData, setCalculationData] = useState<any>(null);
  const [pscData, setPscData] = useState<any>(null); // P_SC data
  const [loadingPsc, setLoadingPsc] = useState<boolean>(false);
  const [loadingProgress, setLoadingProgress] = useState<string>(''); // Progressive loading status

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (!uploadId) return;

    const loadSupercapCalculations = async () => {
      setLoading(true);
      setError(null);
      setLoadingProgress('Lade Daten...');

      try {
        const data = await apiClient.get(`/api/supercap-calculations/${uploadId}`);
        console.log('🟢 Supercap Calculations API Response:', data);
        console.log('📊 Power Histogram Data:', data?.calculations?.power_histogram);
        console.log('📈 Power Statistics:', data?.calculations?.power_statistics);

        setCalculationData(data);
        setLoadingProgress('Daten vollständig geladen!');

        // Clear progress message after 2 seconds
        setTimeout(() => setLoadingProgress(''), 2000);
      } catch (err: any) {
        console.error('Failed to load supercap calculations:', err);
        setError(err.message || 'Fehler beim Laden der Super-Kondensator Berechnungen');
      } finally {
        setLoading(false);
      }
    };

    loadSupercapCalculations();
  }, [uploadId]);

  // Load P_SC data when Energy Analysis tab is selected
  useEffect(() => {
    if (tabValue === 1 && uploadId && !pscData) {
      const loadPscData = async () => {
        setLoadingPsc(true);
        setError(null);
        try {
          const response = await apiClient.get(`/api/advanced/moving-average/${uploadId}?window_size=20`);
          console.log('📊 P_SC API Response:', response);
          if (response && response.data) {
            setPscData(response.data);
          }
        } catch (err: any) {
          console.error('Failed to load P_SC data:', err);
          // Don't set global error, just show info in the P_SC section
        } finally {
          setLoadingPsc(false);
        }
      };
      loadPscData();
    }
  }, [tabValue, uploadId, pscData]);

  if (loading) {
    return (
      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" color="text.secondary">
            Super-Kondensator Berechnungen werden geladen...
          </Typography>
          {loadingProgress && (
            <Typography variant="body2" color="primary">
              {loadingProgress}
            </Typography>
          )}
          <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 2 }} />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom>Fehler beim Laden</Typography>
        <Typography variant="body1">{error}</Typography>
      </Alert>
    );
  }

  const calculations = calculationData?.calculations;
  const references = calculations?.references;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        background: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(20px)',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
      }}
    >
      {/* Paper Reference Banner */}
      {references && (
        <Box
          sx={{
            p: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Info sx={{ color: theme.palette.primary.main }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight="600">
              {references.paper}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {references.authors} • Gleichungen: {references.equations_used}
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="supercap calculations tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            icon={<ElectricBolt />}
            label="Spannungsebenen"
            {...a11yProps(0)}
            iconPosition="start"
          />
          <Tab
            icon={<Battery80 />}
            label="Energie-Analyse"
            {...a11yProps(1)}
            iconPosition="start"
          />
          <Tab
            icon={<Engineering />}
            label="Modul-Auslegung"
            {...a11yProps(2)}
            iconPosition="start"
          />
          <Tab
            icon={<BarChart />}
            label="Statistische Analyse"
            {...a11yProps(3)}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Voltage Levels Tab */}
      <TabPanel value={tabValue} index={0}>
        <Typography variant="h5" fontWeight="700" gutterBottom>
          Spannungsebenen des Super-Kondensator Moduls
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Berechnung der Betriebsspannungen basierend auf Arrhenius-Gleichung und Lebensdauer-Modellierung
        </Typography>

        {calculations?.voltage_levels && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)',
                  border: `1px solid ${theme.palette.divider}`,
                  height: '100%'
                }}
              >
                <Typography variant="subtitle1" fontWeight="600" gutterBottom display="flex" alignItems="center">
                  <ElectricBolt sx={{ mr: 1, color: theme.palette.primary.main }} />
                  Zell-Spannungen
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Maximale Zellspannung (UC01max)
                    </Typography>
                    <Typography variant="h4" fontWeight="800" color="primary">
                      {calculations.voltage_levels.U_C01max} V
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Nominale Zellspannung (UC0N1)
                    </Typography>
                    <Typography variant="h4" fontWeight="800" color="info.main">
                      3.0 V
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Serie
                    </Typography>
                    <Typography variant="h6" fontWeight="600">
                      EDLC 235
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(22, 163, 74, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(22, 163, 74, 0.05) 100%)',
                  border: `1px solid ${theme.palette.divider}`,
                  height: '100%'
                }}
              >
                <Typography variant="subtitle1" fontWeight="600" gutterBottom display="flex" alignItems="center">
                  <Battery80 sx={{ mr: 1, color: theme.palette.success.main }} />
                  Modul-Spannungen
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Maximale Modulspannung (UC0max)
                    </Typography>
                    <Typography variant="h4" fontWeight="800" color="success.main">
                      {calculations.voltage_levels.U_C0max} V
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Minimale Modulspannung (UC0min)
                    </Typography>
                    <Typography variant="h4" fontWeight="800" color="warning.main">
                      {calculations.voltage_levels.U_C0min} V
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Nominale Modulspannung (UC0N)
                    </Typography>
                    <Typography variant="h6" fontWeight="600">
                      {calculations.voltage_levels.U_C0N} V
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%)',
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                  Modul-Konfiguration
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Zellen in Reihe
                      </Typography>
                      <Typography variant="h3" fontWeight="800" color="info.main">
                        {calculations.voltage_levels.N_cells_series}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        EDLC-Zellen
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Maximale Leistung
                      </Typography>
                      <Typography variant="h3" fontWeight="800" color="error.main">
                        {calculations.voltage_levels.P_max_W}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Watt
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    GLEICHUNGEN VERWENDET:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    • Arrhenius-Gleichung für UC01max<br />
                    • Anzahl Zellen in Reihe (N = ⌈U_bus / UC01max⌉)<br />
                    • Minimale Betriebsspannung (UC0min = P_max / IC0max)
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Voltage vs Lifetime Chart - IECON2023 Figure 8 */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom display="flex" alignItems="center">
                  <Battery80 sx={{ mr: 1, color: theme.palette.info.main }} />
                  Zellspannung vs. Lebensdauer
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Vergleich zweier Super-Kondensator Serien (235 ELDC-HVR und 225 ELDC-R) bei T₀ = 65°C
                </Typography>
                <VoltageLifetimeChart
                  selectedVoltage={parseFloat(calculations.voltage_levels.U_C01max)}
                  selectedSeries="ELDC-HVR"
                />
              </Paper>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Energy Analysis Tab */}
      <TabPanel value={tabValue} index={1}>
        <Typography variant="h5" fontWeight="700" gutterBottom>
          Energie-Extremwerte
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Maximale Entlade- und Ladeenergie mittels Moving-Average Filter (Gleichungen 17-19)
        </Typography>

        {calculations?.energy_analysis && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 2,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(220, 38, 38, 0.05) 100%)',
                  border: `2px solid ${theme.palette.error.main}40`
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  Max. Entladeenergie
                </Typography>
                <Typography variant="h3" fontWeight="800" color="error.main" sx={{ my: 2 }}>
                  {calculations.energy_analysis.E_DCH_max_J}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Joule (EDCH,max)
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 2,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(22, 163, 74, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(22, 163, 74, 0.05) 100%)',
                  border: `2px solid ${theme.palette.success.main}40`
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  Max. Ladeenergie
                </Typography>
                <Typography variant="h3" fontWeight="800" color="success.main" sx={{ my: 2 }}>
                  {calculations.energy_analysis.E_CH_max_J}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Joule (ECH,max)
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 2,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(79, 70, 229, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(79, 70, 229, 0.05) 100%)',
                  border: `2px solid ${theme.palette.primary.main}40`
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  Nutzbare Energie
                </Typography>
                <Typography variant="h3" fontWeight="800" color="primary.main" sx={{ my: 2 }}>
                  {calculations.energy_analysis.W_SC_used_J}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Joule (WSC,used)
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                  Analyse-Details
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Fenster-Größe:</strong> {calculations.energy_analysis.window_seconds}s
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Detektierte Zyklen:</strong> {calculations.energy_analysis.cycles_detected}
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    GLEICHUNGEN VERWENDET:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    • Moving Average Filter (Glättungsfenster {calculations.energy_analysis.window_seconds}s)<br />
                    • Zero-Crossing Detection für Lade-/Entladezyklen<br />
                    • Energieintegration über Zeit für jeden Zyklus
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* P_SC(t) Visualization - IECON2023 Figure 8 */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom display="flex" alignItems="center">
                  <ElectricBolt sx={{ mr: 1, color: theme.palette.error.main }} />
                  Supercap-Leistungsverlauf P_SC(t)
                </Typography>
                <Divider sx={{ my: 2 }} />

                {loadingPsc && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Lade Supercap-Analyse...
                    </Typography>
                  </Box>
                )}

                {!loadingPsc && pscData && (
                  <ZoomableCanvas title="Supercap-Leistungsverlauf P_SC(t)">
                    <SupercapPowerChart
                      data={pscData}
                      title=""
                      height={400}
                    />
                  </ZoomableCanvas>
                )}

                {!loadingPsc && !pscData && (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                      P_SC(t) Daten nicht verfügbar
                    </Typography>
                    <Typography variant="body2">
                      Die Supercap-Leistungsverteilung konnte nicht geladen werden. Dies kann auftreten, wenn:
                    </Typography>
                    <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0 }}>
                      <li>Das Backend-Endpunkt `/api/advanced/moving-average` nicht verfügbar ist</li>
                      <li>Die Datenanalyse für diesen Upload noch nicht abgeschlossen ist</li>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Die grundlegenden Energie-Analysen (E_DCH,max, E_CH,max, W_SC,used) sind jedoch verfügbar und können für die Modul-Auslegung verwendet werden.
                    </Typography>
                  </Alert>
                )}
              </Paper>
            </Grid>

            {/* Energy Ratio vs Discharge Ratio Chart - IECON2023 Figure 6 */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom display="flex" alignItems="center">
                  <BarChart sx={{ mr: 1, color: theme.palette.primary.main }} />
                  Energieverhältnis vs. Entladerate
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Theoretische Effizienz-Kurve: V_SC,used/W_total = 1 - r_Q². Optimale Auslegung bei r_Q = 0.7 (50% Energienutzung)
                </Typography>
                <EnergyRatioChart selectedDischargeRatio={0.7} />
              </Paper>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Module Design Tab */}
      <TabPanel value={tabValue} index={2}>
        <Typography variant="h5" fontWeight="700" gutterBottom>
          Modul-Auslegung
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Berechnung der Zwischenspannung und benötigten Modulkapazität 
        </Typography>

        {calculations?.module_design && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)'
                    : 'linear-gradient(135deg, rgba(168, 85, 247, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
                  border: `2px solid ${theme.palette.secondary.main}60`,
                  textAlign: 'center'
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  Zwischenbetriebsspannung
                </Typography>
                <Typography variant="h2" fontWeight="900" color="secondary.main" sx={{ my: 2 }}>
                  {calculations.module_design.U_C0inM_V}
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Volt (UC0inM)
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ textAlign: 'left', mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Berechnet aus:
                  </Typography>
                  <Typography variant="body2">
                    • EDCH,max = {calculations.energy_analysis?.E_DCH_max_J} J<br />
                    • ECH,max = {calculations.energy_analysis?.E_CH_max_J} J<br />
                    • UC0max = {calculations.voltage_levels?.U_C0max} V<br />
                    • UC0min = {calculations.voltage_levels?.U_C0min} V
                  </Typography>
                </Box>

                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="caption" fontWeight="600">
                    GLEICHUNG 16:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 1 }}>
                    UC0inM = √[(EDCH,max×UC0max² + ECH,max×UC0min²) / (EDCH,max + ECH,max)]
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)'
                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(37, 99, 235, 0.08) 100%)',
                  border: `2px solid ${theme.palette.info.main}60`,
                  textAlign: 'center'
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  Benötigte Modulkapazität
                </Typography>
                <Typography variant="h2" fontWeight="900" color="info.main" sx={{ my: 2 }}>
                  {calculations.module_design.C0_F}
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Farad (C0)
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ textAlign: 'left', mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Parameter:
                  </Typography>
                  <Typography variant="body2">
                    • WSC,used = {calculations.energy_analysis?.W_SC_used_J} J<br />
                    • K (End-of-Life) = {calculations.module_design.K_factor}<br />
                    • UC0max = {calculations.voltage_levels?.U_C0max} V<br />
                    • UC0min = {calculations.voltage_levels?.U_C0min} V
                  </Typography>
                </Box>

                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="caption" fontWeight="600">
                    GLEICHUNG 21:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 1 }}>
                    C0 = 2×WSC,used / [K×(UC0max² - UC0min²)]
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                  Hinweis zur Modulkapazität
                </Typography>
                <Typography variant="body2">
                  Die berechnete Kapazität C0 = {calculations.module_design.C0_F} F berücksichtigt den
                  Kapazitätsverlust am Ende der Lebensdauer (K = {calculations.module_design.K_factor}).
                  Dies entspricht einer Kapazitätsabnahme von {((1 - calculations.module_design.K_factor) * 100).toFixed(0)}%
                  über die Betriebsdauer von 40.000 Stunden.
                </Typography>
              </Alert>
            </Grid>

            {/* Breaking Energy Consideration Chart - IECON2023 */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom display="flex" alignItems="center">
                  <Engineering sx={{ mr: 1, color: theme.palette.warning.main }} />
                  Bremsenergie-Betrachtung 
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Vergleich zweier Module: Modul 1 nutzt E_DCH,max + E_CH,max, Modul 2 nutzt E_DCH,max + E_max (Bremsenergie)
                </Typography>
                {calculations?.energy_analysis && calculations?.breaking_energy && (
                  <>
                    <BreakingEnergyChart
                      E_DCH_max={parseFloat(calculations.energy_analysis.E_DCH_max_J)}
                      E_CH_max={parseFloat(calculations.energy_analysis.E_CH_max_J)}
                      P_0={parseFloat(calculations.breaking_energy.P_0_W)}
                      t_br={parseFloat(calculations.breaking_energy.t_br_s)}
                      E_max={parseFloat(calculations.breaking_energy.E_max_J)}
                    />

                    {/* Breaking Energy Statistics */}
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="600">
                        BREMSENERGIE-ANALYSE:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        • Maximale Bremsleistung (P₀): {calculations.breaking_energy.P_0_W} W<br />
                        • Bremszeit (t_br): {calculations.breaking_energy.t_br_s} s<br />
                        • Maximale Bremsenergie (E_max): {calculations.breaking_energy.E_max_J} J<br />
                        • Erkannte Bremsvorgänge: {calculations.breaking_energy.breaking_events}
                      </Typography>
                    </Box>
                  </>
                )}
                {calculations?.energy_analysis && !calculations?.breaking_energy && (
                  <Alert severity="info">
                    Keine Bremsvorgänge (negative Leistung) in den Daten erkannt.
                    Bremsenergie-Analyse nicht verfügbar.
                  </Alert>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Statistical Analysis Tab */}
      <TabPanel value={tabValue} index={3}>
        <Typography variant="h5" fontWeight="700" gutterBottom>
          Statistische Leistungsspitzen-Analyse
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Box-Plot Analyse mit IQR-basierter Ausreißererkennung 
        </Typography>

        {/* Debug Info */}
        {(() => {
          console.log('🔍 Tab 4 Render:', {
            hasCalculations: !!calculations,
            hasPowerStats: !!calculations?.power_statistics,
            hasPowerHistogram: !!calculations?.power_histogram,
            powerStatsKeys: calculations?.power_statistics ? Object.keys(calculations.power_statistics) : null,
            histogramKeys: calculations?.power_histogram ? Object.keys(calculations.power_histogram) : null
          });
          return null;
        })()}

        {calculations?.power_statistics && (
          <Grid container spacing={3}>
            {/* Quartile Box-Plot Visual */}
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(30, 30, 40, 0.6)'
                    : 'rgba(255, 255, 255, 0.95)',
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <Typography variant="subtitle1" fontWeight="600" gutterBottom textAlign="center">
                  Box-Plot Quartilanalyse
                </Typography>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Q1 (25%)</Typography>
                      <Typography variant="h5" fontWeight="700" color="primary">
                        {calculations.power_statistics.Q1} W
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Q2 (Median)</Typography>
                      <Typography variant="h5" fontWeight="700" color="info.main">
                        {calculations.power_statistics.Q2_median} W
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Q3 (75%)</Typography>
                      <Typography variant="h5" fontWeight="700" color="warning.main">
                        {calculations.power_statistics.Q3} W
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Größter Ausreißer</Typography>
                      <Typography variant="h5" fontWeight="700" color="success.main">
                        {calculations.power_statistics.biggest_outlier} W
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Outlier Detection */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                  Ausreißer-Grenzen
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Untere Grenze (Q1 - 1.5×IQR)
                    </Typography>
                    <Typography variant="h6" fontWeight="600">
                      {calculations.power_statistics.lower_bound} W
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Obere Grenze (Q3 + 1.5×IQR)
                    </Typography>
                    <Typography variant="h6" fontWeight="600" color="error.main">
                      {calculations.power_statistics.upper_bound} W
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="caption" fontWeight="600">
                    GLEICHUNGEN 13-14:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    IQR = Q3 - Q1<br />
                    lower = Q1 - 1.5×IQR<br />
                    upper = Q3 + 1.5×IQR
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Peak Statistics */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                  Leistungsspitzen-Statistik
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Gesamtanzahl Ausreißer
                    </Typography>
                    <Typography variant="h4" fontWeight="700" color="warning.main">
                      {calculations.power_statistics.outliers_count}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Hohe Leistungsspitzen (&gt; obere Grenze)
                    </Typography>
                    <Typography variant="h4" fontWeight="700" color="error.main">
                      {calculations.power_statistics.high_power_peaks_count}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Maximale Leistungsspitze
                    </Typography>
                    <Typography variant="h5" fontWeight="600">
                      {calculations.power_statistics.max_power_peak} W
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Minimale Leistung
                    </Typography>
                    <Typography variant="h5" fontWeight="600">
                      {calculations.power_statistics.min_power} W
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Top Outliers */}
            {calculations.power_statistics.outlier_values &&
             calculations.power_statistics.outlier_values.length > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    Top 10 Leistungsspitzen (Ausreißer)
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {calculations.power_statistics.outlier_values.map((value: number, index: number) => (
                      <Chip
                        key={index}
                        label={`${value.toFixed(0)} W`}
                        color="error"
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Power Histogram - IECON2023 Figure 9 (Left) */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                {calculations?.power_histogram ? (
                  <PowerHistogram
                    histogramData={calculations.power_histogram}
                    statistics={{
                      upper_bound: calculations.power_statistics?.upper_bound,
                      Q3: calculations.power_statistics?.Q3
                    }}
                    title="Leistungsverteilung (Histogram)"
                    height={400}
                  />
                ) : (
                  <Alert severity="warning">
                    <Typography variant="subtitle2" gutterBottom>Histogram-Daten nicht verfügbar</Typography>
                    <Typography variant="body2">
                      Debug: {JSON.stringify({ hasCalculations: !!calculations, hasHistogram: !!calculations?.power_histogram })}
                    </Typography>
                  </Alert>
                )}
              </Paper>
            </Grid>

            {/* Box Plot Chart - IECON2023 Figure 9 (Right) */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                <BoxPlotChart
                  statistics={calculations.power_statistics}
                  title="Box-Plot Analyse"
                  height={400}
                />
              </Paper>
            </Grid>
          </Grid>
        )}
      </TabPanel>
    </Paper>
  );
};

export default SupercapCalculationsPanel;
